/**
 * FacetedSearchNav Component
 */

import { getTaxonomyTitle, TaxonomyType } from '../../../../relay/selectors/taxonomy';
import { taxonomiesFragment } from '../../../../relay/trackSearch';
import './styles.scss';

import { find, has, indexOf, isEqual, negate, sortBy } from 'lodash';
import * as React from 'react';
import * as BEM from 'react-bem-helper';
import onClickOutside, { HandleClickOutside, InjectedOnClickOutProps } from 'react-onclickoutside';
import { connect } from 'react-redux';
import { createFragmentContainer } from 'react-relay';
import { VelocityTransitionGroup } from 'velocity-react';

import { QueryStringContext } from '../../../../contexts/queryString';
import {
  getBudgetFilter,
  getExcludedSearchFilters,
  getSearchFilters,
  getTempoFilter,
  hasSearchFilters,
} from '../../../../contexts/queryString/selectors';
import Button from '../../../atoms/buttons/Button';
import { IconX } from '../../../atoms/images/Icon';
import FacetMenuBudget from '../FacetMenu/FacetMenuBudget';
import FacetMenuTempo from '../FacetMenu/FacetMenuTempo';
import FacetMenuTerms from '../FacetMenu/FacetMenuTerms';

interface IOwnStateProps {
  taxonomies: GQL.ITaxonomy[];
  topOffsetPx: number;
  headerType: HeaderType;
}

interface IOwnQueryStringProps {
  budgetFilter: GQL.ITrackSearchBudgetFilter | undefined;
  excludedSearchFilters: SearchFilter[];
  hasFilters: boolean;
  searchFilters: SearchFilter[];
  tempoFilter: GQL.IIntegerRangeFilter | undefined;
}

interface IOwnProps {
  viewer: GQL.IViewer | null;
  currency: GQL.Currency;
  onToggleFacetMenu?(state?: boolean): any;
}

type FacetedSearchNavProps =
  & IOwnProps
  & IOwnStateProps
  & IOwnQueryStringProps
  & InjectedOnClickOutProps
  & HandleClickOutside<HTMLDivElement>;

interface IOwnState {
  selectedTaxonomy: string;
}

interface IInternalTaxonomy {
  id: TaxonomyType;
  internal_id?: number;
  name: string;
  isCustom?: boolean;
}

export type CustomTaxonomyTypes = 'Budget' | 'Tempo';

const customTaxonomies: IInternalTaxonomy[] = [
  {
    id: TaxonomyType.Budget,
    name: TaxonomyType.Budget,
    isCustom: true,
  },
  {
    id: TaxonomyType.Tempos,
    name: 'Tempo',
    isCustom: true,
  },
];

const facetedSearchExcludedTaxonomies: TaxonomyType[] = [
  TaxonomyType.ProductType,
  TaxonomyType.FeMood,
  TaxonomyType.Tags,
  TaxonomyType.Languages,
  TaxonomyType.Lyrics,
  TaxonomyType.References,
  TaxonomyType.Catalogs,
  TaxonomyType.RecordingCountry,
];

const facetedSearchTaxonomiesSortOrder: TaxonomyType[] = [
  TaxonomyType.Budget,
  TaxonomyType.Moods,
  TaxonomyType.Genres,
  TaxonomyType.Instruments,
  TaxonomyType.Industries,
  TaxonomyType.Tags,
  TaxonomyType.Vocals,
  TaxonomyType.Tempos,
];

const defaultObject = {};
const classes = new BEM('faceted-search-nav');
class FacetedSearchNav extends React.Component<FacetedSearchNavProps, IOwnState> {
  taxonomies: IInternalTaxonomy[];

  constructor(props: FacetedSearchNavProps) {
    super(props);

    this.taxonomies = this.getValidTaxonomies();

    this.state = {
      selectedTaxonomy: '',
    };
  }

  componentDidUpdate(prevProps: FacetedSearchNavProps, prevState: IOwnState) {
    if (this.props.viewer) {
      if (prevProps.viewer && !isEqual(
        prevProps.viewer.trackSearch!.taxonomy_cardinalities,
        this.props.viewer.trackSearch!.taxonomy_cardinalities,
      ) || !!!prevProps) {
        this.taxonomies = this.getValidTaxonomies();
      }
    }

    if (prevState.selectedTaxonomy !== this.state.selectedTaxonomy) {
      const shouldOpenFacetMenu = this.state.selectedTaxonomy !== '';
      if (this.props.onToggleFacetMenu) {
        this.props.onToggleFacetMenu(shouldOpenFacetMenu);
      }

      if (shouldOpenFacetMenu) {
        this.props.enableOnClickOutside();
      } else {
        this.props.disableOnClickOutside();
      }
    } else if (
      this.props.searchFilters.length > prevProps.searchFilters.length
      || this.props.excludedSearchFilters.length > prevProps.excludedSearchFilters.length
      || !isEqual(this.props.budgetFilter, prevProps.budgetFilter)
      || !isEqual(this.props.tempoFilter, prevProps.tempoFilter)) {
      this.onToggleFacetMenu('');
    }
  }

  handleClickOutside() {
    this.onRequestCloseFacetMenu();
  }

  onRequestCloseFacetMenu = () => {
    this.onToggleFacetMenu(this.state.selectedTaxonomy as TaxonomyType);
  }

  onShouldCloseFacetMenu = () => new Promise((resolve) => {
    this.onRequestCloseFacetMenu();
    setTimeout(() => {
      resolve();
    }, 1000);
  })

  onToggleFacetMenu(taxonomyId: TaxonomyType | '') {
    if (this.state.selectedTaxonomy === '') {
      this.setState({ selectedTaxonomy: taxonomyId });
    } else if (taxonomyId === this.state.selectedTaxonomy) {
      if (this.props.onToggleFacetMenu) {
        this.props.onToggleFacetMenu(false);
      }
      this.setState({ selectedTaxonomy: '' });
    } else {
      this.setState({ selectedTaxonomy: taxonomyId });
    }
  }

  renderNavigation() {
    const taxonomyCardinalities: ITaxonomyCardinalities = this.props.viewer ?
      this.props.viewer.trackSearch!.taxonomy_cardinalities!
      : defaultObject;

    const title = this.props.hasFilters ? 'Filter' : 'Search by';

    return (
      <ul {...classes('taxonomy-list')} data-title={title}>
        {/*<li {...classes('taxonomy-list__title') }><Span variant="note" size="small">Filter by:</Span></li>*/}
        {this.taxonomies.map(({ id, internal_id, isCustom, name }) => (
          <li key={id!}>
            <Button
              onClick={() => this.onToggleFacetMenu(id!)}
              size="large"
              variant="text"
              label={getTaxonomyTitle(name! as TaxonomyType)}
              icon={id === this.state.selectedTaxonomy && <IconX />}
              iconPosition="right"
              disabled={this.props.hasFilters && !isCustom && !has(taxonomyCardinalities, internal_id!)}
              {...classes('button', { 'is-active': id === this.state.selectedTaxonomy }) }
            />
          </li>
        ))}
      </ul>
    );
  }

  renderFacetMenu(termCardinalities: ITermCardinalities) {
    const taxonomy = find(this.taxonomies, ({ id }) => id === this.state.selectedTaxonomy)!;
    switch (taxonomy.id) {
      case 'Budget':
        return this.renderFacetMenuForBudget();
      case 'Tempo':
        return this.renderFacetMenuForTempo();
      default:
        return this.renderFacetMenuForTerms(termCardinalities);
    }
  }

  renderFacetMenuForBudget() {
    return (
      <FacetMenuBudget
        key={`facet-menu-budget`}
        currency={this.props.currency}
        {...classes('facet-menu')}
        onOpenLicenseBuilder={() => this.props.disableOnClickOutside()}
        onCloseLicenseBuilder={() => this.props.enableOnClickOutside()}
      />
    );
  }

  renderFacetMenuForTempo() {
    return <FacetMenuTempo key={`facet-menu-tempo`} {...classes('facet-menu') } />;
  }

  renderFacetMenuForTerms(termCardinalities: ITermCardinalities) {
    const taxonomy = this.getSelectedTaxonomy();
    const allTerms = this.getTermsForSelectedTaxonomy(taxonomy, termCardinalities);

    return (
      <FacetMenuTerms
        key={`facet-menu-${taxonomy.id}`}
        shouldCloseFacetMenu={this.onShouldCloseFacetMenu}
        taxonomyName={taxonomy.name!}
        terms={allTerms}
        termCardinalities={termCardinalities}
        {...classes('facet-menu') }
      />
    );
  }

  render() {
    const { viewer, topOffsetPx, headerType } = this.props;
    const { selectedTaxonomy } = this.state;

    const termCardinalities: ITermCardinalities = viewer ?
      viewer.trackSearch!.term_cardinalities!
      : defaultObject;

    const staticTopOffset = 64;
    // topOffsetPx changes based on license bar height
    const offset = {
      simple: staticTopOffset + topOffsetPx,
      extended: 0,
      hybrid: staticTopOffset,
    };
    const topOffset = offset[headerType];
    return (
      <aside {...classes()} style={{top: topOffset + 'px'}}>
        <VelocityTransitionGroup
          component="div"
          enter={{
            animation: {
              // translateX: [0, '-20%'],
              translateZ: [0, -50],
              // rotateY: ['0deg', '-30deg'],
              opacity: 1,
            },
            duration: 500,
            delay: 400,
            easing: [50, 10],
            display: 'flex',
          }}
          leave={{
            animation: {
              translateZ: 50,
              opacity: 0,
            },
            duration: 150,
          }}
          {...classes('facet-menu-wrapper') }
        >
          {selectedTaxonomy !== '' && this.renderFacetMenu(termCardinalities)}
        </VelocityTransitionGroup>
        {this.renderNavigation()}
      </aside>
    );
  }

  private getValidTaxonomies(props = this.props): IInternalTaxonomy[] {
    const taxonomyIsExcluded = ({ name }: GQL.ITaxonomy) => indexOf(facetedSearchExcludedTaxonomies, name!) > -1;
    const convertToInternalTaxonomy = ({ id, internal_id, name }: GQL.ITaxonomy) => ({
      id: id as TaxonomyType,
      internal_id: internal_id!,
      name: name!,
    });
    const sortTaxonomiesByName = (a: IInternalTaxonomy, b: IInternalTaxonomy) =>
      facetedSearchTaxonomiesSortOrder.indexOf(a.name as TaxonomyType) >
      facetedSearchTaxonomiesSortOrder.indexOf(b.name as TaxonomyType) ?
        1 : -1;

    return props.taxonomies.filter(negate(taxonomyIsExcluded))
        .map<IInternalTaxonomy>(convertToInternalTaxonomy)
        .concat(customTaxonomies)
        .sort(sortTaxonomiesByName);
  }

  private getSelectedTaxonomy() {
    return find(this.props.taxonomies, { id: this.state.selectedTaxonomy })!;
  }

  private getTermsForSelectedTaxonomy({ terms }: GQL.ITaxonomy, termCardinalities: ITermCardinalities) {
    const validTerms = terms!.filter(({ internal_id }) =>
      this.props.hasFilters ?
        has(termCardinalities, internal_id!)
        : true,
    );

    return sortBy(validTerms, 'name');
  }
}

const FacetedSearchNavWithOnClickOutside = onClickOutside<IOwnProps & IOwnQueryStringProps>(FacetedSearchNav);

const ConnectedFacetedSearchNav = connect<IOwnStateProps, {}, IOwnProps & IOwnQueryStringProps, IStoreState>(
  (state) => ({
    taxonomies: state.staticData.taxonomies,
    topOffsetPx: state.ui.licenseBar.heightPx,
    headerType: state.ui.header.type,
  }),
)(FacetedSearchNavWithOnClickOutside);

const RelayFacetedSearchNav = createFragmentContainer(
  ConnectedFacetedSearchNav,
  taxonomiesFragment,
);

const FacetedSearchNavWithQueryStringContext: React.SFC<IOwnProps> = (props) => (
  <QueryStringContext.Consumer>
    {(ctx) => (
      <RelayFacetedSearchNav
        budgetFilter={getBudgetFilter(ctx)}
        excludedSearchFilters={getExcludedSearchFilters(ctx)}
        hasFilters={hasSearchFilters(ctx)}
        searchFilters={getSearchFilters(ctx)}
        tempoFilter={getTempoFilter(ctx)}
        {...props}
      />
    )}
  </QueryStringContext.Consumer>
);

export default FacetedSearchNavWithQueryStringContext;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/public/components/organisms/search/FacetedSearchNav/index.tsx